import { useTranslation } from 'react-i18next';
import { IconComparador } from '../Icons/IconComparador';
import { IconPending } from '../Icons/IconPending';
import './PriceComparatorSaved.css';

import { useEffect, useState } from 'react';
import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';
import { useRemainingPacks } from '../../hooks/useRemainingPacks';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import { IconCircleInfo } from '../Icons';
import IconPro from '../Icons/IconPro';
import PopUp from '../../shared/PopUp/PopUp';
import { SuscripcionesPacks } from '../../pages/Suscripciones/SuscripcionesPacks';
import { SuscripcionesChout } from '../../pages/Suscripciones/SuscripcionesChout';
import { SuscripcionesChoutSucces } from '../../pages/Suscripciones/SuscripcionesChoutSucces';
import { ProductChildViewModel, ProductViewModelCollectionResult, postApiSubscriptionProducts } from '../../api';
import { useGetCalculations } from './hooks/useGetCalculations';
import { FriendlyDate } from '../FriendlyDate/FriendlyDate';
import { useProUser } from '../../context/ProUserContext';

interface Calculation {
	reference: string;
	tariffCodes: string;
	countries: string;
	createdDateUTC: string;
	statusDescription: string;
}

const PriceComparatorSaved = ({
	setCurrentStep,
	setCurrentPack,
}: {
	setCurrentStep: (step: string) => void;
	setCurrentPack: (pack: { userSubscriptionId: string; productCount?: number; countryCount?: number }) => void;
}) => {
	const { t } = useTranslation();
	const {
		data: remainingPacks,
		loading: loadingCalcs,
		error: errorCalcs,
		refetch: refetchRemainingCalcs,
	} = useRemainingPacks(process.env.REACT_APP_PRICE_COMPARATOR_ID || '354');

	// Hook para verificar estado pro del usuario
	const { refetchProStatus } = useProUser();

	const [isOpen, setIsOpen] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState<number>(0);
	const [childProducts, setChildProducts] = useState<ProductChildViewModel[]>([]);
	const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
	const [step, setStep] = useState(1);
	const [isOpenCorp, setIsOpenCorp] = useState(false);
	const [isCheckOut, setIsCheckOut] = useState(false);
	const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
	const [referenceFilter, setReferenceFilter] = useState<string>('');
	const [tariffCodeFilter, setTariffCodeFilter] = useState<string>('');
	const [dateFilter, setDateFilter] = useState<string>('');
	const { data: calculations, loading: loadingCalculations, error: errorCalculations } = useGetCalculations(sortOrder);

	// Filtrar cálculos por referencia, código de tarifa y fecha
	const filteredCalculations =
		calculations?.results?.filter((calculation: Calculation) => {
			const matchesReference = calculation.reference.toLowerCase().includes(referenceFilter.toLowerCase());
			const matchesTariffCode = calculation.tariffCodes.toLowerCase().includes(tariffCodeFilter.toLowerCase());

			// Filtrar por fecha si se ha seleccionado una fecha
			let matchesDate = true;
			if (dateFilter) {
				const calculationDate = new Date(calculation.createdDateUTC);
				const filterDate = new Date(dateFilter);
				// Comparar solo la fecha (sin hora)
				const calculationDateOnly = calculationDate.toISOString().split('T')[0];
				const filterDateOnly = filterDate.toISOString().split('T')[0];
				matchesDate = calculationDateOnly === filterDateOnly;
			}

			return matchesReference && matchesTariffCode && matchesDate;
		}) || [];

	// Función para manejar el cambio de ordenamiento
	const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSortOrder(event.target.value as 'ASC' | 'DESC');
	};

	// Función para calcular días restantes desde la fecha de creación hasta 15 días después
	const calculateRemainingDays = (createdDateUTC: string): number => {
		const createdDate = new Date(createdDateUTC);
		const deadlineDate = new Date(createdDate.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 días adicionales = 15 días total
		const now = new Date();

		const timeDiff = deadlineDate.getTime() - now.getTime();
		const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

		return Math.max(0, daysDiff); // No mostrar días negativos
	};

	// Función para obtener el texto de progreso
	const getProgressText = (createdDateUTC: string): string => {
		const remainingDays = calculateRemainingDays(createdDateUTC);

		if (remainingDays === 0) {
			return t('price_comparator.step_saved.progress_finished');
		} else if (remainingDays === 1) {
			return t('price_comparator.step_saved.progress_one_day_left');
		} else {
			return t('price_comparator.step_saved.progress_days_left', { days: remainingDays });
		}
	};

	const handlePacksClick = () => {
		setIsOpen(true);
	};

	const handleCloseCorp = () => setIsOpenCorp(false);
	const handleCloseCheckOut = () => {
		setIsCheckOut(false);
		setIsOpen(false);
	};

	const handleCompareClick = () => {
		if ((remainingPacks?.availableSubscriptionsCount ?? 0) > 1) {
			setCurrentStep('packs');
		} else {
			// Si solo hay un pack disponible, establecerlo como currentPack y ir a detail
			// Filtrar solo las suscripciones con usos disponibles
			const availablePacks = remainingPacks?.results?.filter((pack) => (pack.remainingUses ?? 0) > 0) || [];

			// console.log('Todas las suscripciones:', remainingPacks);
			// console.log('Solo suscripciones disponibles:', availablePacks);

			const singlePack = availablePacks[0];
			// console.log(singlePack);
			if (singlePack) {
				setCurrentPack({
					userSubscriptionId: singlePack.userSubscriptionId,
					productCount: singlePack.productCount,
					countryCount: singlePack.countryCount,
				});
			}
			setCurrentStep('detail');
		}
	};

	// Cargar todos los productos disponibles
	useEffect(() => {
		const loadProducts = async () => {
			try {
				setLoadingProducts(true);
				const data: ProductViewModelCollectionResult = await postApiSubscriptionProducts();
				if (data.results) {
					// Encontrar el producto "Price Comparator"
					const priceComparatorProduct = data.results.find((product) => product.name === 'Price Comparator');
					if (priceComparatorProduct?.childProducts) {
						setChildProducts(priceComparatorProduct.childProducts);
					}
				}
			} catch (error) {
				console.error('Error al cargar los productos:', error);
			} finally {
				setLoadingProducts(false);
			}
		};

		loadProducts();
	}, []);

	useEffect(() => {
		console.log('🔄 Refreshing pro status after comparison');
		refetchProStatus();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	if (loadingCalcs || loadingProducts) {
		return <CircularLoading />;
	}
	return (
		<>
			<div className="price-calc__row">
				<div className="exporter-test-information__header price-calc__row__header">
					<div className="price-calc__principal-title">
						<span>{t('price_comparator.header.title')}</span>
						<CustomToolTip
							title=""
							tooltipText={
								<span>
									{t('price_comparator.header.tooltip')}{' '}
									<button className="price-calc__tootip-link">{t('price_comparator.header.button')}</button>
								</span>
							}>
							<IconCircleInfo />
						</CustomToolTip>
						{(remainingPacks?.availableSubscriptionsCount ?? 0) > 0 ? (
							<span className="price-calc__label price-calc__label--pro">
								<IconPro
									fill="#1A2228"
									height={12}
									width={15}
								/>
								<span className="price-calc__label__text">
									{remainingPacks?.availableSubscriptionsCount}{' '}
									{(remainingPacks?.availableSubscriptionsCount ?? 0) === 1
										? t('price_comparator.step_saved.pack_available')
										: t('price_comparator.step_saved.packs_available')}
								</span>
							</span>
						) : (
							<span className="price-calc__label">
								<span className="price-calc__label__text">{t('price_comparator.step_saved.pack_name')}</span>
							</span>
						)}
					</div>

					{(remainingPacks?.availableSubscriptionsCount ?? 0) > 0 ? (
						<button
							className="price-calc__button"
							onClick={handleCompareClick}>
							{t('price_comparator.step_saved.button_compare')}
						</button>
					) : (
						<button
							className="price-calc__button"
							onClick={handlePacksClick}>
							{t('price_comparator.step_saved.button_compare')}
							<div className="price-calc__button__icon">
								<IconPro
									fill="#1A2228"
									height={12}
									width={15}
								/>
							</div>
						</button>
					)}
				</div>
			</div>
			<div className="price-calc__row">
				<div className="calculo-container">
					<div className="calculo-container__overflow">
						<div className="calculo-container__top">
							<div className="calculo-container__top__item">
								<input
									type="text"
									className="calculo-container__top__input"
									value={referenceFilter}
									onChange={(e) => setReferenceFilter(e.target.value)}
								/>
								<div className="calculo-container__top__item__label">{t('price_comparator.step_saved.search')}</div>
							</div>
							<div className="calculo-container__top__item">
								<input
									type="text"
									className="calculo-container__top__input"
									value={tariffCodeFilter}
									onChange={(e) => setTariffCodeFilter(e.target.value)}
								/>
								<div className="calculo-container__top__item__label">{t('price_comparator.step_saved.tarif_code')}</div>
							</div>
							<div className="calculo-container__top__item">
								<input
									type="date"
									className="calculo-container__top__input"
									value={dateFilter}
									onChange={(e) => setDateFilter(e.target.value)}
								/>
								<div className="calculo-container__top__item__label">{t('price_comparator.step_saved.date')}</div>
							</div>
						</div>
						<div className="calculo-container__middle">
							<div className="calculo-container__middle__item">
								<div className="calculo-container__middle__item__title">
									{filteredCalculations.length} {t('price_comparator.step_saved.calculations')}
								</div>
								<div className="calculo-container__middle__item__description">
									<select
										className="calculo-container__middle__item__select"
										value={sortOrder}
										onChange={handleSortChange}>
										<option value="DESC">
											{t('price_comparator.step_saved.sort_by')} {t('price_comparator.step_saved.sort_by_recent')}
										</option>
										<option value="ASC">
											{t('price_comparator.step_saved.sort_by')} {t('price_comparator.step_saved.sort_by_oldest')}
										</option>
									</select>
								</div>
							</div>
						</div>

						<div className="calculo-container__bottom">
							<div className="calculo-container__bottom__row calculo-container__bottom__row--header">
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--reference">
									{t('price_comparator.step_saved.reference')}
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--arancel">
									{t('price_comparator.step_saved.arancel')}
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--countries">
									{t('price_comparator.step_saved.countries')}
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--date">
									{t('price_comparator.step_saved.date')}
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--progress">
									{t('price_comparator.step_saved.progress')}
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--actions"></div>
							</div>

							{/* TODO: Agregar cuando este lo del reporte */}
							{filteredCalculations.map((calculation: Calculation) => (
								<div className="calculo-container__bottom__row calculo-container__bottom__row--item">
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--reference">
										<div
											className={`calculo-container__bottom__row__item__icon ${calculation.statusDescription == 'Informe finalizado' ? 'done' : ''}`}>
											{calculation.statusDescription == 'Informe finalizado' ? <IconComparador /> : <IconPending />}
										</div>
										{calculation.reference}
									</div>
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--arancel">
										{calculation.tariffCodes}
									</div>
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--countries">
										{calculation.countries}
									</div>
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--date">
										<FriendlyDate date={calculation.createdDateUTC} />
									</div>
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--progress">
										{calculation.statusDescription == 'Informe finalizado' ? (
											<span className="calculo-container__bottom__row__item__progress__text done">
												{t('price_comparator.step_saved.progress_finished')}
											</span>
										) : (
											<span
												className={`calculo-container__bottom__row__item__progress__text ${calculateRemainingDays(calculation.createdDateUTC) === 0 ? 'done' : ''}`}>
												{getProgressText(calculation.createdDateUTC)}
											</span>
										)}
									</div>
									<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--actions">
										{calculateRemainingDays(calculation.createdDateUTC) === 0 && (
											<button onClick={() => setCurrentStep('report')}>
												{t('price_comparator.step_saved.view_details')}
											</button>
										)}
									</div>
								</div>
							))}

							{/*
							<div className="calculo-container__bottom__row calculo-container__bottom__row--item">
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--reference">
									<div className="calculo-container__bottom__row__item__icon">
										<IconPending />
									</div>
									Chocolates 80% cacao 1454
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--arancel">
									231231212421, 231231212421
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--countries">
									<span>Francia, </span>
									<span>Dinamarca</span>
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--date">
									11 de junio, 19:46
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--progress">
									<span className="calculo-container__bottom__row__item__progress__text">Faltan 3 días</span>
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--actions"></div>
							</div>
							<div className="calculo-container__bottom__row calculo-container__bottom__row--item">
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--reference">
									<div className="calculo-container__bottom__row__item__icon done">
										<IconComparador />
									</div>
									Chocolates 80% cacao 1454
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--arancel">
									0202201091
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--countries">
									<span>España, </span>
									<span>Alemania, </span>
									<span>Francia, </span>
									<span>Dinamarca</span>
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--date">
									11 de junio, 19:46
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--progress">
									<span className="calculo-container__bottom__row__item__progress__text done">Finalizado</span>
								</div>
								<div className="calculo-container__bottom__row__item calculo-container__bottom__row__item--actions">
									<button onClick={() => setCurrentStep('report')}>
										{t('price_comparator.step_saved.view_details')}
									</button>
								</div>
							</div> */}
						</div>
					</div>
				</div>
			</div>

			{/* PopUp Corporativo */}
			{isOpenCorp && (
				<PopUp
					isOpen={isOpenCorp}
					onClose={handleCloseCorp}
					classes="popup-corp">
					<div className="popup_corp_container">
						<h3 className="popup_corp_title">{t('suscripcion.modal_corpo.title')}</h3>
						<div
							className="popup_corp_body"
							dangerouslySetInnerHTML={{
								__html: t('suscripcion.modal_corpo.body', {
									email: localStorage.getItem('username') || 'usuario@mail.com',
								}),
							}}
						/>
						<div className="popup_corp_footer">
							<button
								className="popup_corp_button"
								onClick={handleCloseCorp}
								type="button">
								{t('suscripcion.modal_corpo.button')}
							</button>
						</div>
					</div>
				</PopUp>
			)}

			{/* PopUp Checkout */}
			{isCheckOut && step === 2 && (
				<PopUp
					isOpen={isCheckOut}
					onClose={handleCloseCheckOut}
					classes="popup-checkout">
					<SuscripcionesChout
						setStep={setStep}
						setIsOpen={setIsOpen}
						productId={selectedProductId}
						data={childProducts}
						productParentName="PriceComparator"
					/>
				</PopUp>
			)}

			{/* PopUp Checkout Success */}
			{isCheckOut && step === 3 && (
				<PopUp
					isOpen={isCheckOut}
					onClose={handleCloseCheckOut}
					classes="popup-message">
					<SuscripcionesChoutSucces
						setIsCheckOut={setIsCheckOut}
						setIsOpen={setIsOpen}
						setStep={setStep}
						refetch={refetchRemainingCalcs}
					/>
				</PopUp>
			)}

			{/* PopUp Packs */}
			{isOpen && !isCheckOut && !isOpenCorp && (
				<PopUp
					isOpen={isOpen}
					onClose={() => setIsOpen(false)}
					classes="popup-packs">
					<SuscripcionesPacks
						setStep={setStep}
						setIsOpenCorp={setIsOpenCorp}
						setIsCheckOut={setIsCheckOut}
						setIsOpen={setIsOpen}
						setProductId={setSelectedProductId}
						data={childProducts}
						productIdsUses={[]}
						productParentName="PriceComparator"
					/>
				</PopUp>
			)}
		</>
	);
};

export default PriceComparatorSaved;
